// F40 — Helpers de formato de fecha y número parametrizados por locale.
// Antes había `toLocaleDateString('es-ES', ...)` esparcido por la app, lo que
// rompía la i18n. Toda la app debe ir a través de estos helpers.
import i18n from '../i18n'

const TAG_MAP: Record<string, string> = {
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-PT',
}

export function currentLocaleTag(): string {
  const lang = (i18n.language || 'es').toLowerCase().split('-')[0] ?? 'es'
  return TAG_MAP[lang] ?? 'es-ES'
}

export function formatDate(
  value: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(currentLocaleTag(), opts)
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(currentLocaleTag(), opts)
}

export function formatTimeIntl(
  value: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(currentLocaleTag(), opts)
}

export function formatNumber(
  value: number | null | undefined,
  opts: Intl.NumberFormatOptions = {},
): string {
  if (value == null || Number.isNaN(value)) return ''
  return value.toLocaleString(currentLocaleTag(), opts)
}

/** Moneda — siempre euros (la app es EUR-only por contexto fiscal ES). */
export function formatCurrencyFromCents(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return ''
  return (cents / 100).toLocaleString(currentLocaleTag(), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // 'always' fuerza miles también en 4 cifras (es-ES no agrupa por defecto).
    ...({ useGrouping: 'always' } as unknown as Intl.NumberFormatOptions),
  })
}

export function formatRelative(value: string | number | Date | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const rtf = new Intl.RelativeTimeFormat(currentLocaleTag(), { numeric: 'auto' })
  const diffMs = d.getTime() - Date.now()
  const diffMin = Math.round(diffMs / 60_000)
  const diffH = Math.round(diffMs / 3_600_000)
  const diffD = Math.round(diffMs / 86_400_000)
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute')
  if (Math.abs(diffH) < 24) return rtf.format(diffH, 'hour')
  return rtf.format(diffD, 'day')
}
